// Audio endpoints (/api/conversation/{id}/audio, .../messages/{messageId}/audio, /api/message-annotation/{id}/audio)
// need the Authorization header, which an <audio src> cannot send. The file is fetched with axios (which adds the
// header) and played from a blob URL.
//
// request({signal}): a repository call returning the axios blob response, e.g.
//   ({signal}) => ConversationRepository.getConversationAudio({id, signal})
// load(): fetches once and resolves the blob URL (concurrent calls share the request); null after dispose().
// dispose(): aborts a pending request and revokes the blob URL - call it when the player unmounts.
export const createAudioLoader = (request) => {
  const controller = new AbortController();
  let objectUrl = null;
  let pending = null;
  let disposed = false;

  return {
    load() {
      if (disposed) return Promise.resolve(null);
      if (objectUrl) return Promise.resolve(objectUrl);
      if (!pending) {
        pending = request({signal: controller.signal})
          .then(response => {
            if (disposed) return null;
            objectUrl = URL.createObjectURL(response.data);
            return objectUrl;
          })
          .catch(error => {
            // a failed load can be retried
            pending = null;
            throw error;
          });
      }
      return pending;
    },
    dispose() {
      disposed = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    },
  };
};

// Gives an <audio> element its source from the loader (once), then the caller can play it
export const ensureAudioSource = async (audio, loader) => {
  if (!audio || !loader) return false;
  if (audio.getAttribute("src")) return true;
  const url = await loader.load();
  if (!url) return false;
  audio.src = url;
  return true;
};

export const isAbortError = (error) => error?.name === "CanceledError" || error?.name === "AbortError";
